import { Directive } from '@angular/core';
import { AlgoliaResponse } from '@marmon/frontend/pagination';
import { AlgoliaEntity } from '@marmon/shared/types/algolia';
import { Entity } from '@marmon/shared/types/common';
import { PageSize, SortMod } from '@marmon/shared/types/pagination';
import { TuiTablePaginationEvent } from '@taiga-ui/addon-table';
import { tuiIsPresent } from '@taiga-ui/cdk';
import {
	BehaviorSubject,
	combineLatest,
	debounceTime,
	filter,
	map,
	Observable,
	startWith,
	switchMap,
	tap,
} from 'rxjs';
import { CrudService } from '../../services';
import { TableBaseComponent } from '../table-base/table-base.component';

@Directive()
export class AlgoliaPaginatedBaseComponent<
	AlgoliaType extends AlgoliaEntity = AlgoliaEntity,
	ObjectType extends Entity = Entity,
> extends TableBaseComponent<AlgoliaType> {
	protected readonly page$ = new BehaviorSubject(0);

	/**
	 * Hook to attach any additional logic before the request starts in this page. Allows
	 * the implementation of the component to alter any of the options, or provide any
	 * additional logic that might be required before the data is fetched
	 *
	 * @param options the options that are generated from the paginated page
	 */
	protected readonly beforeRequestStarts$ = (
		options: [
			string,
			keyof AlgoliaType | null,
			SortMod,
			number,
			PageSize,
			void,
		],
	) =>
		new Observable<
			[string, keyof AlgoliaType | null, SortMod, number, PageSize, void]
		>().pipe(startWith(options));

	/**
	 * Hook to attach any additional logic before the request ends in this page. Allows
	 * the implementation of the component to alter any of the results, or provide any
	 * additional logic that might be required after the request was made but before the
	 * loading has finished.
	 *
	 * @param results the results that were fetched from the Algolia request.
	 */
	protected readonly beforeRequestEnd$ = (
		results: AlgoliaResponse<AlgoliaType>,
	) =>
		new Observable<AlgoliaResponse<AlgoliaType>>().pipe(startWith(results));

	private readonly request$ = combineLatest([
		this.search$,
		this.sorter$,
		this.direction$,
		this.page$,
		this.size$,
		this.refetch$.pipe(debounceTime(500), startWith(undefined)),
	]).pipe(
		tap(() => this.loading$.next(true)),
		// some time debounce for a case when both key and direction change
		debounceTime(250),
		switchMap((options) => this.beforeRequestStarts$(options)),
		switchMap(([query, sortBy, sortDir, page, size]) =>
			this.crudService.fetchAlgolia({
				query,
				sortBy,
				sortDir,
				page,
				size,
			}),
		),
		switchMap((results) => this.beforeRequestEnd$(results)),
		tap(() => this.loading$.next(false)),
	);

	protected readonly total$ = this.request$.pipe(
		filter(tuiIsPresent),
		filter((response) => !!response),
		map(({ nbHits }) => nbHits),
		startWith(0),
	);

	protected readonly data$: Observable<readonly AlgoliaType[]> =
		this.request$.pipe(
			filter(tuiIsPresent),
			map(({ hits }) => hits.filter(tuiIsPresent)),
			startWith([]),
		);

	constructor(private crudService: CrudService<ObjectType>) {
		super();
	}

	protected onPagination({ page, size }: TuiTablePaginationEvent) {
		this.page$.next(page);
		this.size$.next(size as PageSize);
	}
}
